import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
// не использовать ui-компоненты в сторе
import { notification } from 'antd';
import * as dateFns from 'date-fns';
import {
  EventsGeoJSONPropertiesResponse,
  EventsMunicipalDistricts,
  EventsResponse,
  EventsResponseProps,
  MunicipalDistrict,
} from '@project/API/resources/events';
import { axiosInstance, Services } from '../../API';
import { MapProperties } from '@datum-map';
import { QueryOptions, Query } from '../Query';
import { SENSITIVE_VALUES } from '../../components/Module/Table/Filters/FilterInput/constants';
import axios from 'axios';
import { SingleWindow } from '../Window';
import { IEventsItemProperties } from './types';
import { EventsLayer } from './EventsLayer';
import { Point } from 'ol/geom';
import WKT from 'ol/format/WKT';
import { LoadFeatureCollectionCallback } from '../SourceLoaderDecorator';
import { FilterSearchGroups } from '../Query/Filter';
import { QueryToVectorSourceLink } from './QueryToVectorSourceLink';

export type EventsFilteredFields =
  | 'title'
  | 'is_checked'
  | 'is_archived'
  | 'date_start'
  | 'date_end'
  | 'municipal_district__title';

const DEFAULT_QUERY: QueryOptions<EventsFilteredFields> = {
  page: 1,
  pageSize: 10,
  search: undefined,
  order: [{ field: 'date_start' }],
  filter: {
    orParams: [
      [
        {
          field: 'date_end',
          operator: SENSITIVE_VALUES.gte,
          value: dateFns.format(new Date(), 'yyyy-MM-dd'),
        },
        { field: 'is_checked', value: 'true' },
        { field: 'is_archived', value: 'false' },
      ],
    ],
  },
  isLimitOffsetPagination: true,
};

/* TODO: Отображать статус для каждой отдельной новости */
export class EventsStore {
  readonly query: Query<EventsFilteredFields>;
  readonly eventCard = new SingleWindow<IEventsItemProperties>();
  readonly eventsAdd = new SingleWindow<GeoJSON.Position>();
  readonly queryLayerLink: QueryToVectorSourceLink<EventsFilteredFields>;
  readonly layerController: EventsLayer;

  @observable private _isLoading = false;
  @observable private _events: EventsResponse[] = [];
  @observable private _error?: string = undefined;
  @observable private _totalEventsCount = 0;
  @observable private _eventsMunicipalDistricts: EventsMunicipalDistricts;
  @observable private _eventsDistricts: MunicipalDistrict[];

  private _services: Services;
  private _mapProperties: MapProperties;
  private _eventsLoaderSignals = new Set<AbortController>();

  constructor(services: Services, mapProperties: MapProperties) {
    this._services = services;
    this._mapProperties = mapProperties;
    this._events = [];
    this._eventsDistricts = [];
    this._eventsMunicipalDistricts = new EventsMunicipalDistricts(
      axiosInstance,
    );

    this.layerController = new EventsLayer(
      this._loadFeatures,
      this._mapProperties.map,
      { mode: 'viewing' },
    );
    this.query = new Query<EventsFilteredFields>(DEFAULT_QUERY);
    this.queryLayerLink = new QueryToVectorSourceLink(
      this.query,
      this.layerController.source,
    );

    this.queryLayerLink.bind();

    this.loadMunicipalDistricts();

    makeObservable(this);
  }

  @computed get events() {
    return this._events;
  }

  @computed get eventsDistricts() {
    return this._eventsDistricts;
  }

  @computed get totalEventsCount() {
    return this._totalEventsCount;
  }

  @computed get error() {
    return this._error;
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  showEventsOnMap() {
    return this.layerController.show();
  }

  @action loadEvents = async (params?: URLSearchParams | string) => {
    if (this._isLoading) {
      this._eventsLoaderSignals.forEach((s) => s.abort());
      this._eventsLoaderSignals = new Set();
    }

    const currentSignal = new AbortController();
    this._eventsLoaderSignals.add(currentSignal);

    runInAction(() => {
      this._isLoading = true;
      this._error = undefined;
    });

    try {
      const result = await this._services.events.get(
        params,
        currentSignal.signal,
      );

      runInAction(() => {
        this._events = result['features'];
        this._totalEventsCount = result._count;
      });
    } catch (e) {
      if (!axios.isCancel(e)) {
        runInAction(() => {
          this._events = [];
          this._error = 'Произошла ошибка при загрузке афиши';
        });
      }
    } finally {
      this._eventsLoaderSignals.delete(currentSignal);

      runInAction(() => {
        this._isLoading = this._eventsLoaderSignals.size > 0;
      });
    }
  };

  @action addEvent = async (event: EventsResponseProps) => {
    const response = await this._services.events.create(event);

    runInAction(() => {
      this._events.push(response);
    });
    // @ts-ignore
    if (response.id && event.photos && event.photos.fileList) {
      event.id = response.id;
      // @ts-ignore
      await this._services.eventsPhotos.post(event, response.id);
    }

    // @ts-ignore
    const updResponse = await this._services.events.get({ id: response.id });

    if (updResponse.features.length === 0) {
      throw new Error('Событие не найдено');
    }

    const index = this._events.findIndex((item) => item.id === event.id);
    this._events[index] = updResponse.features[0];

    runInAction(() => {
      this._totalEventsCount = this._totalEventsCount + 1;
    });

    this.loadEvents(this.query.searchParams);

    if (this.layerController.isVisible) {
      this.layerController.source.refresh();
    }

    this.eventsAdd.close();
  };

  @action editEvent = async (
    event: Partial<Omit<EventsGeoJSONPropertiesResponse, 'geom'>> & {
      id: number;
      geom?: GeoJSON.Point;
    },
  ) => {
    const view = this._mapProperties.map.getView();
    const projection = view.getProjection().getCode();
    const wkt = event.geom
      ? this._geomToWkt(event.geom, projection)
      : undefined;

    const response = await this._services.events.patch({
      ...event,
      geom: wkt,
    });

    // @ts-ignore
    if (response.id && event.photos && event.photos.fileList) {
      event.id = response.id;
      // @ts-ignore
      await this._services.eventsPhotos.post(event, response.id);
    }

    // @ts-ignore
    if (event.photos && event.photos.removedFiles) {
      // @ts-ignore
      await this._services.eventsPhotos.delete(event);
    }

    this.loadEvents(this.query.searchParams);

    if (this.layerController.isVisible) {
      this.layerController.source.refresh();
    }
  };

  @action editEventGeometry = async (event: {
    id: number;
    geom?: string | GeoJSON.Point | Point;
  }) => {
    const view = this._mapProperties.map.getView();
    const projection = view.getProjection().getCode();
    const wkt = event.geom
      ? this._geomToWkt(event.geom, projection)
      : undefined;

    try {
      const response = await this._services.events.patch({
        ...event,
        geom: wkt,
      });

      if (response.geometry) {
        this.layerController.source.refresh();
      }
    } catch (e) {
      notification.error({ title: 'Не удалось изменить событие' });
    }
  };

  @action deleteEvents = async (id: number) => {
    await this._services.events.delete(id);

    this.layerController.source.refresh();

    const item = this._events.findIndex((v) => v.id === id);

    if (item === -1) return;

    this._events.splice(item, 1);

    runInAction(() => {
      this._totalEventsCount = this._totalEventsCount - 1;
    });

    await this._updatePage();
  };

  private _updatePage = async () => {
    const isEmptyPage = this._events.length === 0;
    const hasEventsItems = this._totalEventsCount > 0;

    if (!isEmptyPage || (isEmptyPage && this.query.page === 1)) {
      const result = await this._services.events.get(this.query.searchParams);

      runInAction(() => {
        this._events = result.features;
        // this._totalEventsCount = result.count;
      });

      return;
    }

    // Запрос будет отправлен в useEffect
    if (hasEventsItems && this.query.page > 1) {
      this.query.setPage(this.query.page - 1);
    }
  };

  @action loadMunicipalDistricts = async () => {
    if (this._isLoading) {
      this._eventsLoaderSignals.forEach((s) => s.abort());
      this._eventsLoaderSignals = new Set();
    }

    const currentSignal = new AbortController();
    this._eventsLoaderSignals.add(currentSignal);

    runInAction(() => {
      this._isLoading = true;
      this._error = undefined;
    });

    try {
      const result = await this._eventsMunicipalDistricts.get();

      runInAction(() => {
        // @ts-ignore
        this._eventsDistricts = result;
      });
    } catch (e) {
      if (!axios.isCancel(e)) {
        runInAction(() => {
          this._eventsDistricts = [];
          this._error = 'Произошла ошибка при загрузке афиши';
        });
      }
    } finally {
      this._eventsLoaderSignals.delete(currentSignal);

      runInAction(() => {
        this._isLoading = this._eventsLoaderSignals.size > 0;
      });
    }
  };

  private _geomToWkt = (
    geom: Point | GeoJSON.Point | string,
    fromProj: string,
  ): string => {
    if (typeof geom === 'string') {
      return geom;
    }

    const toProj = 'EPSG:4326';

    if (geom instanceof Point) {
      const geomIn4326 = geom.clone().transform(fromProj, toProj);
      const wktGeometry = new WKT().writeGeometry(geomIn4326);

      return 'SRID=4326;' + wktGeometry;
    }

    const olGeom = new Point(geom.coordinates);
    const geomIn4326 = olGeom.clone().transform(fromProj, toProj);
    const wktGeometry = new WKT().writeGeometry(geomIn4326);

    return 'SRID=4326;' + wktGeometry;
  };

  private _loadFeatures: LoadFeatureCollectionCallback = async (config) => {
    const params = new URLSearchParams(config.params.toString());
    const extraParams = this._buildExtraParams();

    if (extraParams) {
      extraParams.forEach((value, key) => {
        params.set(key, value);
      });
    }

    const response = await this._services.eventsGeoJSONLayer.get({
      ...config,
      params,
    });

    return response;
  };

  private _buildExtraParams = () => {
    if (!this.query) return undefined;

    const { filter, search } = this.query;

    const filterSearchParams = new FilterSearchGroups(filter.value).toString();
    const params = new URLSearchParams(filterSearchParams);

    if (search) params.set('_search', search);

    return params;
  };
}
